/**
 * DateTime utilities for UTC <-> Pacific Time conversion
 *
 * All timestamps are stored in UTC in the database.
 * All displays should be in Pacific Time (America/Los_Angeles).
 */

const PACIFIC_TZ = 'America/Los_Angeles';

/**
 * Format a UTC ISO string to Pacific Time for display
 * @param isoString - UTC ISO timestamp string
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted string in Pacific Time
 */
export function formatToPacific(
  isoString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!isoString) return '';

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';

  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: PACIFIC_TZ,
    ...options,
  };

  return date.toLocaleString('en-US', defaultOptions);
}

/**
 * Format a UTC ISO string to Pacific date only (e.g., "Jan 10, 2026")
 */
export function formatDatePacific(isoString: string | null | undefined): string {
  return formatToPacific(isoString, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a UTC ISO string to Pacific time only (e.g., "2:45 PM")
 */
export function formatTimePacific(isoString: string | null | undefined): string {
  return formatToPacific(isoString, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a UTC ISO string to Pacific datetime (e.g., "Jan 10, 2026, 2:45 PM")
 */
export function formatDateTimePacific(isoString: string | null | undefined): string {
  return formatToPacific(isoString, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Format a UTC ISO string to Pacific with day of week (e.g., "Fri, Jan 10, 2:45 PM")
 */
export function formatWithDayPacific(isoString: string | null | undefined): string {
  return formatToPacific(isoString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get the current time as an ISO string in UTC
 */
export function nowUTC(): string {
  return new Date().toISOString();
}

/**
 * Convert a Pacific date and time to UTC ISO string
 * @param pacificDate - Date string in YYYY-MM-DD format
 * @param pacificTime - Time string in HH:MM format (24-hour)
 * @returns UTC ISO string
 */
export function pacificToUTC(pacificDate: string, pacificTime: string): string {
  // Create a date string that JavaScript will parse
  const dateTimeStr = `${pacificDate}T${pacificTime}:00`;

  // Calculate Pacific offset at the TARGET date (not now) to handle DST boundaries correctly.
  // A date in January needs UTC-8 (PST), a date in July needs UTC-7 (PDT).
  const targetDate = new Date(dateTimeStr);
  const utcStr = targetDate.toLocaleString('en-US', { timeZone: 'UTC' });
  const pacificStr = targetDate.toLocaleString('en-US', { timeZone: PACIFIC_TZ });
  const offset = new Date(utcStr).getTime() - new Date(pacificStr).getTime();

  // Parse the input as local and adjust for Pacific offset
  const pacificAsUtc = new Date(targetDate.getTime() + offset);

  return pacificAsUtc.toISOString();
}

/**
 * Extract Pacific date (YYYY-MM-DD) from a UTC ISO string
 */
export function getDateInPacific(isoString: string): string {
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date); // Returns YYYY-MM-DD
}

/**
 * Extract Pacific time (HH:MM) from a UTC ISO string
 */
export function getTimeInPacific(isoString: string): string {
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: PACIFIC_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return formatter.format(date); // Returns HH:MM
}

/**
 * Check if a timestamp is within N hours of now
 */
export function isWithinHours(isoString: string, hours: number): boolean {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours >= 0 && diffHours <= hours;
}

/**
 * Check if a timestamp is within 24 hours of now
 */
export function isWithin24Hours(isoString: string): boolean {
  return isWithinHours(isoString, 24);
}

// =============================================================================
// Pacific Date Utilities
// =============================================================================
// USE THESE instead of new Date().toISOString().split('T')[0] which gives UTC!
// Example: If it's 11 PM Pacific on Jan 10, UTC is already Jan 11.
// - new Date().toISOString().split('T')[0] → "2026-01-11" (WRONG for Pacific)
// - getTodayPacific() → "2026-01-10" (CORRECT for Pacific)
// =============================================================================

/**
 * Get today's date in Pacific timezone as YYYY-MM-DD string.
 * USE THIS instead of new Date().toISOString().split('T')[0]!
 */
export function getTodayPacific(): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(now); // Returns YYYY-MM-DD
}

/**
 * Format a Date object to YYYY-MM-DD in Pacific timezone.
 * USE THIS instead of date.toISOString().split('T')[0]!
 */
export function formatDateISOPacific(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: PACIFIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date); // Returns YYYY-MM-DD
}

/**
 * Get the current day of week in Pacific timezone (0 = Sunday, 6 = Saturday)
 */
export function getDayOfWeekPacific(): number {
  return getDayOfWeekForDatePacific(getTodayPacific());
}

// Calendar-day arithmetic on 'YYYY-MM-DD' strings is done in UTC with Date.UTC, so it
// never depends on the browser's timezone (a local-noon Date is still the PREVIOUS
// Pacific day in browsers at UTC+5 or further east) and has no DST drift.
// Known limit: years 0000-0099 are rejected below (Date.UTC reads them as 19xx, so the
// round-trip check fails). Irrelevant for schedule dates.
function parseCalendarDay(dateStr: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) throw new RangeError(`Invalid calendar day: ${dateStr}`);
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const ms = Date.UTC(y, mo - 1, d);
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    throw new RangeError(`Invalid calendar day: ${dateStr}`);
  }
  return ms;
}

/**
 * Get the day of week for a specific date string in Pacific timezone
 * @param dateStr - Date in YYYY-MM-DD format
 * @returns Day of week (0 = Sunday, 6 = Saturday)
 */
export function getDayOfWeekForDatePacific(dateStr: string): number {
  return new Date(parseCalendarDay(dateStr)).getUTCDay();
}

/**
 * Get start of week (Monday) for current Pacific date as YYYY-MM-DD
 */
export function getWeekStartPacific(): string {
  return getWeekStartForDatePacific(getTodayPacific());
}

/**
 * Get start of week (Monday) for a specific date as YYYY-MM-DD
 * @param dateStr - Date in YYYY-MM-DD format
 */
export function getWeekStartForDatePacific(dateStr: string): string {
  const dayOfWeek = getDayOfWeekForDatePacific(dateStr);
  // Sunday (0) → -6, Monday (1) → 0, Tuesday (2) → -1, etc.
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return addDays(dateStr, -daysToSubtract);
}

/**
 * Add days to a date string (YYYY-MM-DD) and return new YYYY-MM-DD
 */
export function addDays(dateStr: string, days: number): string {
  const t = new Date(parseCalendarDay(dateStr) + days * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(t.getUTCFullYear()).padStart(4, '0')}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/**
 * Check if a date string (YYYY-MM-DD) is today in Pacific timezone
 */
export function isTodayPacific(dateStr: string): boolean {
  return dateStr === getTodayPacific();
}

/**
 * Check if a date string (YYYY-MM-DD) is in the past (before today in Pacific)
 */
export function isPastPacific(dateStr: string): boolean {
  return dateStr < getTodayPacific();
}

/**
 * Check if a date string (YYYY-MM-DD) is in the future (after today in Pacific)
 */
export function isFuturePacific(dateStr: string): boolean {
  return dateStr > getTodayPacific();
}
